import { previewToMessageChanges } from '../../components/pages/AiAnalytics/services/aiDataUtils';
import {
  ASK_FAILED_FALLBACK_MESSAGE,
  ASK_FAST_POLL_MS,
  ASK_FAST_POLL_WINDOW_MS,
  ASK_NO_SQL_MESSAGE,
  ASK_OUTCOMES,
  ASK_REFUSED_FALLBACK_MESSAGE,
  ASK_SLOW_POLL_MS,
  AUTO_TITLE_MAX_CHARS,
  AUTO_TITLE_WORD_COUNT,
  DEFAULT_THREAD_TITLE,
  FAILED_STATUSES,
  FINISHED_STATUSES,
  MESSAGE_ROLES,
  REFUSED_STATUS
} from '../../constants/aiAnalytics';

export const POLL_RUNNING = 'running';

export function classifyAskTask(task) {
  if (FINISHED_STATUSES.includes(task.status)) {
    return ASK_OUTCOMES.FINISHED;
  }
  if (task.status === REFUSED_STATUS) {
    return ASK_OUTCOMES.REFUSED;
  }
  if (FAILED_STATUSES.includes(task.status)) {
    return ASK_OUTCOMES.FAILED;
  }
  return POLL_RUNNING;
}

export function nextAskPollDelay(elapsedMs) {
  return elapsedMs < ASK_FAST_POLL_WINDOW_MS ? ASK_FAST_POLL_MS : ASK_SLOW_POLL_MS;
}

export function diffAskProgress(task, currentAsk) {
  const current = currentAsk || {};
  const changes = {};
  if (task.phase != null && task.phase !== current.phase) {
    changes.phase = task.phase;
  }
  if (task.sql_generation_reasoning != null && task.sql_generation_reasoning !== current.reasoning) {
    changes.reasoning = task.sql_generation_reasoning;
  }
  if (task.rephrased_question != null && task.rephrased_question !== current.question) {
    changes.question = task.rephrased_question;
  }
  return changes;
}

export function isRetryablePollError(error) {
  return !(error.response && error.response.status < 500);
}

export function serverAuthoredMessageId(taskResult) {
  return taskResult.messageId;
}

export function hasGeneratedSql(taskResult) {
  return Boolean(taskResult.response && taskResult.response.length > 0);
}

export function buildAskResultMessage(taskResult, preview) {
  return {
    role: MESSAGE_ROLES.ASSISTANT,
    content: taskResult.rephrased_question || '',
    sql: taskResult.response[0].sql,
    reasoning: taskResult.sql_generation_reasoning || null,
    ...previewToMessageChanges(preview),
    messageId: serverAuthoredMessageId(taskResult)
  };
}

export function buildAskFailureMessage(task) {
  if (classifyAskTask(task) === ASK_OUTCOMES.REFUSED) {
    return { role: MESSAGE_ROLES.ASSISTANT, content: task.error?.message || ASK_REFUSED_FALLBACK_MESSAGE };
  }
  return {
    role: MESSAGE_ROLES.ASSISTANT,
    content: task.error?.message || ASK_FAILED_FALLBACK_MESSAGE,
    isError: true
  };
}

export function buildAskNoSqlMessage() {
  return { role: MESSAGE_ROLES.ASSISTANT, content: ASK_NO_SQL_MESSAGE, isError: true };
}

export function shouldAutoTitle(activeThread) {
  const isFirstMessage = !activeThread?.messages || activeThread.messages.length === 0;
  const title = activeThread?.title;
  return isFirstMessage && (!title || title === DEFAULT_THREAD_TITLE);
}

export function deriveThreadTitle(question) {
  const words = question.trim().split(/\s+/);
  const slice = words.slice(0, AUTO_TITLE_WORD_COUNT).join(' ');
  const truncated = words.length > AUTO_TITLE_WORD_COUNT;
  let title = slice.length > AUTO_TITLE_MAX_CHARS ? slice.slice(0, AUTO_TITLE_MAX_CHARS) : slice;
  if (truncated || slice.length > AUTO_TITLE_MAX_CHARS) {
    title += '…';
  }
  return title;
}

export function delay(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

export async function pollUntil({ poll, classify, delayFor, maxAttempts, deadlineMs, isRetryableError }) {
  const startedAt = Date.now();
  for (let attempt = 0; maxAttempts == null || attempt < maxAttempts; attempt++) {
    const elapsed = Date.now() - startedAt;
    if (deadlineMs != null && elapsed >= deadlineMs) {
      return { timedOut: true };
    }
    await delay(delayFor ? delayFor(elapsed) : 0);
    let result;
    try {
      result = await poll();
    } catch (error) {
      if (isRetryableError && isRetryableError(error)) {
        continue;
      }
      throw error;
    }
    const outcome = classify(result);
    if (outcome !== POLL_RUNNING) {
      return { outcome, result };
    }
  }
  return { timedOut: true };
}

const activePolls = new Set();

export function registerPoll(pollId) {
  if (activePolls.has(pollId)) {
    return false;
  }
  activePolls.add(pollId);
  return true;
}

export function releasePoll(pollId) {
  activePolls.delete(pollId);
}

export function isPolling(pollId) {
  return activePolls.has(pollId);
}

export function resetActivePollsForTests() {
  activePolls.clear();
}
